import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2, UserPlus, X } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const DAYS_SHORT = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const SHIFTS = [
    { id: "", label: "—" },
    { id: "ochtend", label: "Ochtend" },
    { id: "middag", label: "Middag" },
    { id: "nacht", label: "Nacht" },
    { id: "vrij", label: "Vrij" },
];
const SHIFT_SHORT = { ochtend: "O", middag: "M", nacht: "N", vrij: "V" };
const SHIFT_LABEL = {
    ochtend: "Ochtend (07:00–15:00)",
    middag: "Middag (15:00–23:00)",
    nacht: "Nacht (23:00–07:00)",
    vrij: "Vrij",
};
const LEAVE_LABEL = { verlof: "Verlof", ziek: "Ziek", afwezig: "Afw." };

const mondayOf = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
    return x;
};
const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
};
const fmtDay = (d) => d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
const leaveOn = (emp, dayIso) => (emp.leave || []).find((l) => l.from <= dayIso && dayIso <= l.to);

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";

const WeekNav = ({ weekStart, setWeekStart }) => (
    <div className="flex items-center gap-4">
        <button
            data-testid="planning-prev-week"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            aria-label="Vorige week"
            className="border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold"
        >
            <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-44 text-center text-sm tracking-wide text-bone">
            Week van {fmtDay(weekStart)} – {fmtDay(addDays(weekStart, 6))}
        </span>
        <button
            data-testid="planning-next-week"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            aria-label="Volgende week"
            className="border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold"
        >
            <ChevronRight className="h-4 w-4" />
        </button>
    </div>
);

const MyWeek = () => {
    const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            setErr("");
            try {
                const { data } = await platformClient.get(`/planning/my?week=${iso(weekStart)}`);
                setData(data);
            } catch (e) {
                setData(null);
                setErr(formatPlatformError(e.response?.data?.detail));
            }
        })();
    }, [weekStart]);

    return (
        <div data-testid="planning-my-week" className="mt-8">
            <WeekNav weekStart={weekStart} setWeekStart={setWeekStart} />
            {err ? (
                <p data-testid="planning-my-error" className="mt-8 text-sm text-ash">
                    {err === "Geen medewerker gekoppeld aan je account"
                        ? "Je account is nog niet gekoppeld aan een medewerker. Vraag je planner om dit te koppelen."
                        : err}
                </p>
            ) : data ? (
                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
                    {Array.from({ length: 7 }, (_, i) => {
                        const d = addDays(weekStart, i);
                        const dIso = iso(d);
                        const lv = leaveOn(data.employee, dIso);
                        const shift = data.shifts?.[String(i)] || "";
                        return (
                            <div
                                key={i}
                                data-testid={`planning-my-day-${i}`}
                                className={`border p-4 ${shift || lv ? "border-gold/30 bg-coal" : "border-white/10 bg-coal/50"}`}
                            >
                                <p className="text-[10px] uppercase tracking-[0.2em] text-ash">
                                    {DAYS_SHORT[i]} {fmtDay(d)}
                                </p>
                                <p className="mt-3 font-display text-lg text-bone">
                                    {lv ? LEAVE_LABEL[lv.type] || lv.type : shift ? SHIFT_LABEL[shift].split(" (")[0] : "—"}
                                </p>
                                <p className="mt-1 text-xs text-ash">
                                    {lv ? "Niet aan het werk" : shift && shift !== "vrij" ? SHIFT_LABEL[shift].match(/\((.*)\)/)?.[1] : shift === "vrij" ? "Vrij" : "Niet ingepland"}
                                </p>
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

const LeaveAdder = ({ emp, onSaved }) => {
    const [form, setForm] = useState({ from: "", to: "", type: "verlof", note: "" });
    const submit = async (e) => {
        e.preventDefault();
        await platformClient.patch(`/planning/employees/${emp.id}`, {
            leave: [...(emp.leave || []), form],
        });
        setForm({ from: "", to: "", type: "verlof", note: "" });
        onSaved();
    };
    return (
        <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
            <input
                data-testid={`leave-from-${emp.id}`}
                type="date"
                value={form.from}
                onChange={(e) => setForm({ ...form, from: e.target.value })}
                required
                className={`${inputCls} mt-0 [color-scheme:dark]`}
            />
            <input
                data-testid={`leave-to-${emp.id}`}
                type="date"
                value={form.to}
                onChange={(e) => setForm({ ...form, to: e.target.value })}
                required
                className={`${inputCls} mt-0 [color-scheme:dark]`}
            />
            <select
                data-testid={`leave-type-${emp.id}`}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="border-b border-white/20 bg-ink pb-2.5 text-sm text-bone focus:border-gold focus:outline-none"
            >
                <option value="verlof">Verlof</option>
                <option value="ziek">Ziek</option>
                <option value="afwezig">Afwezig</option>
            </select>
            <input
                data-testid={`leave-note-${emp.id}`}
                placeholder="Notitie (optioneel)"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className={`${inputCls} mt-0`}
            />
            <button
                data-testid={`leave-add-${emp.id}`}
                type="submit"
                className="flex items-center justify-center gap-2 border border-gold/50 px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
            >
                <Plus className="h-3.5 w-3.5" /> Verlof
            </button>
        </form>
    );
};

export const PlanningModule = ({ user }) => {
    const isOwner = user.role === "owner";
    const [tab, setTab] = useState("week");
    const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
    const [employees, setEmployees] = useState([]);
    const [shifts, setShifts] = useState({});
    const [monthCursor, setMonthCursor] = useState(() => new Date());
    const [monthCounts, setMonthCounts] = useState({});
    const [error, setError] = useState("");
    const [newEmp, setNewEmp] = useState({ name: "", functie: "", linked_email: "" });
    const [dayIdx, setDayIdx] = useState(0);

    const load = useCallback(async () => {
        if (!isOwner) return;
        try {
            const [emps, sched] = await Promise.all([
                platformClient.get("/planning/employees"),
                platformClient.get(`/planning/schedule?week=${iso(weekStart)}`),
            ]);
            setEmployees(emps.data);
            setShifts(sched.data.shifts || {});
        } catch {}
    }, [isOwner, weekStart]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!isOwner || tab !== "maand") return;
        (async () => {
            const first = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
            const last = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0);
            let monday = mondayOf(first);
            const weeks = [];
            while (monday <= last) {
                weeks.push(monday);
                monday = addDays(monday, 7);
            }
            const results = await Promise.all(
                weeks.map((w) => platformClient.get(`/planning/schedule?week=${iso(w)}`))
            );
            const counts = {};
            results.forEach((r, wi) => {
                const w = weeks[wi];
                const shiftsMap = r.data.shifts || {};
                Object.values(shiftsMap).forEach((days) => {
                    Object.entries(days).forEach(([idx, s]) => {
                        if (s && s !== "vrij") {
                            const dIso = iso(addDays(w, Number(idx)));
                            counts[dIso] = (counts[dIso] || 0) + 1;
                        }
                    });
                });
            });
            setMonthCounts(counts);
        })();
    }, [isOwner, tab, monthCursor]);

    const setShift = async (empId, dayIdx, value) => {
        const next = { ...shifts, [empId]: { ...(shifts[empId] || {}), [String(dayIdx)]: value } };
        if (!value) delete next[empId][String(dayIdx)];
        setShifts(next);
        try {
            await platformClient.put("/planning/schedule", { week: iso(weekStart), shifts: next });
        } catch {}
    };

    const addEmployee = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await platformClient.post("/planning/employees", newEmp);
            setNewEmp({ name: "", functie: "", linked_email: "" });
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const patchEmployee = async (emp, update) => {
        try {
            await platformClient.patch(`/planning/employees/${emp.id}`, update);
            load();
        } catch {}
    };

    const removeEmployee = async (emp) => {
        if (!window.confirm(`${emp.name} verwijderen uit de planning?`)) return;
        try {
            await platformClient.delete(`/planning/employees/${emp.id}`);
            load();
        } catch {}
    };

    if (!isOwner) return <MyWeek />;

    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    const tabs = [
        { id: "week", label: "Weekrooster" },
        { id: "dag", label: "Dagoverzicht" },
        { id: "maand", label: "Maand" },
        { id: "medewerkers", label: `Medewerkers (${employees.length})` },
    ];

    return (
        <div data-testid="planning-module" className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-4">
                <div className="flex gap-6">
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`planning-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`-mb-[17px] border-b-2 pb-4 text-xs uppercase tracking-[0.2em] transition-colors ${
                                tab === t.id
                                    ? "border-gold text-gold"
                                    : "border-transparent text-ash hover:text-bone"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                {tab !== "medewerkers" && tab !== "maand" ? (
                    <WeekNav weekStart={weekStart} setWeekStart={setWeekStart} />
                ) : null}
                {tab === "maand" ? (
                    <div className="flex items-center gap-4">
                        <button
                            data-testid="planning-prev-month"
                            onClick={() =>
                                setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))
                            }
                            aria-label="Vorige maand"
                            className="border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="min-w-36 text-center text-sm capitalize text-bone">
                            {monthCursor.toLocaleDateString("nl-NL", { month: "long", year: "numeric" })}
                        </span>
                        <button
                            data-testid="planning-next-month"
                            onClick={() =>
                                setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))
                            }
                            aria-label="Volgende maand"
                            className="border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                ) : null}
            </div>

            {tab === "week" ? (
                employees.length === 0 ? (
                    <p data-testid="planning-empty" className="mt-8 text-sm text-ash">
                        Voeg eerst medewerkers toe via het tabblad Medewerkers.
                    </p>
                ) : (
                    <div className="mt-6 overflow-x-auto">
                        <table className="w-full min-w-[760px] border-collapse">
                            <thead>
                                <tr>
                                    <th className="pb-3 text-left text-[10px] uppercase tracking-[0.2em] text-ash">
                                        Medewerker
                                    </th>
                                    {weekDays.map((d, i) => (
                                        <th
                                            key={i}
                                            className="pb-3 text-center text-[10px] uppercase tracking-[0.2em] text-ash"
                                        >
                                            {DAYS_SHORT[i]}
                                            <span className="block text-gold/70">{fmtDay(d)}</span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {employees.map((emp) => (
                                    <tr key={emp.id} className="border-t border-white/10">
                                        <td className="py-3 pr-4">
                                            <p className="text-sm text-bone">{emp.name}</p>
                                            <p className="text-xs text-ash">{emp.functie}</p>
                                        </td>
                                        {weekDays.map((d, i) => {
                                            const dIso = iso(d);
                                            const lv = leaveOn(emp, dIso);
                                            const shift = shifts[emp.id]?.[String(i)] || "";
                                            const available = emp.availability?.[String(i)] !== false;
                                            return (
                                                <td key={i} className="px-1 py-3 text-center">
                                                    {lv ? (
                                                        <span
                                                            data-testid={`planning-cell-${emp.id}-${i}`}
                                                            className="inline-block w-full border border-gold/30 bg-gold/10 px-1 py-2 text-[10px] uppercase tracking-wide text-gold"
                                                        >
                                                            {LEAVE_LABEL[lv.type] || lv.type}
                                                        </span>
                                                    ) : (
                                                        <span className="relative block">
                                                            <select
                                                                data-testid={`planning-cell-${emp.id}-${i}`}
                                                                value={shift}
                                                                onChange={(e) => setShift(emp.id, i, e.target.value)}
                                                                className={`w-full border px-1 py-2 text-center text-xs transition-colors focus:border-gold focus:outline-none ${
                                                                    shift
                                                                        ? "border-gold/40 bg-gold/10 text-gold"
                                                                        : available
                                                                          ? "border-white/15 bg-ink text-ash"
                                                                          : "border-white/10 bg-ink/50 text-ash/40"
                                                                }`}
                                                            >
                                                                {SHIFTS.map((s) => (
                                                                    <option key={s.id} value={s.id}>
                                                                        {SHIFT_SHORT[s.id] || "—"}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {!available ? (
                                                                <span className="pointer-events-none absolute -top-2 right-0 text-[8px] uppercase tracking-wide text-ash/50">
                                                                    n.b.
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="mt-4 text-xs text-ash/60">
                            O = Ochtend (07–15) · M = Middag (15–23) · N = Nacht (23–07) · V = Vrij · n.b. = niet
                            beschikbaar die dag
                        </p>
                    </div>
                )
            ) : null}

            {tab === "dag" ? (
                <div className="mt-6">
                    <div className="flex flex-wrap gap-2">
                        {weekDays.map((d, i) => (
                            <button
                                key={i}
                                data-testid={`planning-day-${i}`}
                                onClick={() => setDayIdx(i)}
                                className={`border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
                                    dayIdx === i
                                        ? "border-gold bg-gold/10 text-gold"
                                        : "border-white/15 text-ash hover:border-gold/40 hover:text-bone"
                                }`}
                            >
                                {DAYS_SHORT[i]} {fmtDay(d)}
                            </button>
                        ))}
                    </div>
                    <div className="mt-8 space-y-6">
                        {SHIFTS.filter((s) => s.id && s.id !== "vrij").map((s) => {
                            const who = employees.filter(
                                (emp) => shifts[emp.id]?.[String(dayIdx)] === s.id
                            );
                            return (
                                <div key={s.id} data-testid={`planning-day-shift-${s.id}`}>
                                    <p className="text-[11px] uppercase tracking-[0.25em] text-gold">
                                        {SHIFT_LABEL[s.id]}
                                    </p>
                                    {who.length === 0 ? (
                                        <p className="mt-2 text-sm text-ash/60">Niemand ingepland.</p>
                                    ) : (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {who.map((emp) => (
                                                <span
                                                    key={emp.id}
                                                    className="border border-white/10 bg-coal px-4 py-2 text-sm text-bone"
                                                >
                                                    {emp.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {(() => {
                            const absent = employees.filter((emp) =>
                                leaveOn(emp, iso(weekDays[dayIdx]))
                            );
                            return absent.length ? (
                                <div data-testid="planning-day-absent">
                                    <p className="text-[11px] uppercase tracking-[0.25em] text-ash">
                                        Afwezig
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {absent.map((emp) => (
                                            <span
                                                key={emp.id}
                                                className="border border-gold/30 bg-gold/10 px-4 py-2 text-sm text-gold"
                                            >
                                                {emp.name} —{" "}
                                                {LEAVE_LABEL[leaveOn(emp, iso(weekDays[dayIdx])).type]}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : null;
                        })()}
                    </div>
                </div>
            ) : null}

            {tab === "maand" ? (
                <div className="mt-6 grid grid-cols-7 gap-px bg-white/10">
                    {DAYS_SHORT.map((d) => (
                        <div key={d} className="bg-ink p-2 text-center text-[10px] uppercase tracking-[0.2em] text-ash">
                            {d}
                        </div>
                    ))}
                    {(() => {
                        const first = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
                        const startOffset = (first.getDay() + 6) % 7;
                        const daysInMonth = new Date(
                            monthCursor.getFullYear(),
                            monthCursor.getMonth() + 1,
                            0
                        ).getDate();
                        const cells = [];
                        for (let i = 0; i < startOffset; i++)
                            cells.push(<div key={`b${i}`} className="bg-ink p-3" />);
                        for (let day = 1; day <= daysInMonth; day++) {
                            const d = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), day);
                            const count = monthCounts[iso(d)] || 0;
                            cells.push(
                                <div
                                    key={day}
                                    data-testid={`planning-month-day-${day}`}
                                    className={`min-h-16 bg-ink p-3 ${count ? "border border-gold/20" : ""}`}
                                >
                                    <p className="text-xs text-ash">{day}</p>
                                    {count ? (
                                        <p className="mt-1 text-[10px] uppercase tracking-wide text-gold">
                                            {count} dienst{count === 1 ? "" : "en"}
                                        </p>
                                    ) : null}
                                </div>
                            );
                        }
                        const trailing = (7 - ((startOffset + daysInMonth) % 7)) % 7;
                        for (let i = 0; i < trailing; i++)
                            cells.push(<div key={`t${i}`} className="bg-ink p-3" />);
                        return cells;
                    })()}
                </div>
            ) : null}

            {tab === "medewerkers" ? (
                <div className="mt-6">
                    <form
                        data-testid="planning-employee-form"
                        onSubmit={addEmployee}
                        className="grid grid-cols-1 gap-6 border border-white/10 bg-coal p-6 sm:grid-cols-4"
                    >
                        <label className="block">
                            <span className={labelCls}>Naam</span>
                            <input
                                data-testid="planning-emp-name"
                                value={newEmp.name}
                                onChange={(e) => setNewEmp({ ...newEmp, name: e.target.value })}
                                required
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Functie</span>
                            <input
                                data-testid="planning-emp-functie"
                                value={newEmp.functie}
                                onChange={(e) => setNewEmp({ ...newEmp, functie: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Gekoppelde login e-mail</span>
                            <input
                                data-testid="planning-emp-email"
                                type="email"
                                value={newEmp.linked_email}
                                onChange={(e) => setNewEmp({ ...newEmp, linked_email: e.target.value })}
                                placeholder="voor eigen rooster-weergave"
                                className={inputCls}
                            />
                        </label>
                        <div className="flex items-end">
                            <button
                                data-testid="planning-emp-submit"
                                type="submit"
                                className="flex w-full items-center justify-center gap-2 bg-gold px-4 py-3 text-xs uppercase tracking-[0.15em] text-black transition-colors hover:bg-bone"
                            >
                                <UserPlus className="h-4 w-4" /> Toevoegen
                            </button>
                        </div>
                    </form>
                    {error ? <p className="mt-4 text-sm text-red-400">{error}</p> : null}

                    <div className="mt-6 space-y-4">
                        {employees.map((emp) => (
                            <div
                                key={emp.id}
                                data-testid={`planning-emp-${emp.id}`}
                                className="border border-white/10 bg-coal p-6"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <p className="font-display text-xl">{emp.name}</p>
                                        <p className="mt-1 text-xs text-ash">
                                            {emp.functie || "—"}
                                            {emp.linked_email ? ` · login: ${emp.linked_email}` : ""}
                                        </p>
                                    </div>
                                    <button
                                        data-testid={`planning-emp-delete-${emp.id}`}
                                        onClick={() => removeEmployee(emp)}
                                        aria-label="Verwijderen"
                                        className="border border-white/15 p-2.5 text-ash transition-colors hover:border-red-400/60 hover:text-red-400"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    {DAYS_SHORT.map((d, i) => {
                                        const on = emp.availability?.[String(i)] !== false;
                                        return (
                                            <button
                                                key={i}
                                                data-testid={`planning-avail-${emp.id}-${i}`}
                                                onClick={() =>
                                                    patchEmployee(emp, {
                                                        availability: {
                                                            ...(emp.availability || {}),
                                                            [String(i)]: !on,
                                                        },
                                                    })
                                                }
                                                className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] transition-colors ${
                                                    on
                                                        ? "border-gold/40 text-gold"
                                                        : "border-white/15 text-ash/50 line-through"
                                                }`}
                                            >
                                                {d}
                                            </button>
                                        );
                                    })}
                                </div>
                                {(emp.leave || []).length ? (
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {emp.leave.map((l, li) => (
                                            <span
                                                key={li}
                                                className="flex items-center gap-2 border border-gold/30 bg-gold/10 px-3 py-1.5 text-xs text-gold"
                                            >
                                                {LEAVE_LABEL[l.type] || l.type}: {l.from} → {l.to}
                                                {l.note ? ` (${l.note})` : ""}
                                                <button
                                                    data-testid={`planning-leave-delete-${emp.id}-${li}`}
                                                    onClick={() =>
                                                        patchEmployee(emp, {
                                                            leave: emp.leave.filter((_, i) => i !== li),
                                                        })
                                                    }
                                                    aria-label="Verlof verwijderen"
                                                    className="text-gold/60 transition-colors hover:text-red-400"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                ) : null}
                                <LeaveAdder emp={emp} onSaved={load} />
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};
